import React from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';

export interface FaqEntry {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface FaqAccordionProps {
  items: FaqEntry[];
  /** Item open on first render. */
  defaultOpen?: string;
  className?: string;
}

/**
 * Question/answer list (Figma "FAQ item"), one open at a time. Built on Radix Accordion for
 * keyboard support and aria-expanded/aria-controls wiring.
 */
const FaqAccordion: React.FC<FaqAccordionProps> = ({ items, defaultOpen, className }) => (
  <Accordion.Root
    type="single"
    collapsible
    defaultValue={defaultOpen}
    className={cn('tw-flex tw-flex-col tw-divide-y tw-divide-ds-border-default tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-px-5 tw-py-1', className)}
  >
    {items.map((item) => (
      <Accordion.Item key={item.id} value={item.id} className="tw-group tw-py-4">
        <Accordion.Header>
          <Accordion.Trigger className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded tw-text-left tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand data-[state=open]:tw-text-ds-brand-text">
            <span className="tw-min-w-0 tw-flex-1">{item.question}</span>
            <Icon
              icon={ChevronDown}
              className="tw-text-ds-text-secondary tw-transition-transform tw-duration-200 group-data-[state=open]:tw-rotate-180 group-data-[state=open]:tw-text-ds-brand-text"
            />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className="tw-pt-2.5 tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary">{item.answer}</Accordion.Content>
      </Accordion.Item>
    ))}
  </Accordion.Root>
);

export default FaqAccordion;
